import * as React from 'react'
import classnames from 'classnames'
import { on, off } from '@arsenal/arsenal/modules/event'
import noopWrapper, { Wrapper } from 'lib/noop-wrapper'

interface IOption {
  /**
   * 显示文案
   */
  text: string
  /**
   * 对应值
   */
  value: string | number | boolean
}
interface IOptionGroup {
  label: string
  options: IOption[]
}

type OptionClickHandler = (
  evt: React.MouseEvent,
  option: IOption,
  index: number
) => void
interface IProps {
  /**
   * 选中的option
   */
  value: any
  /**
   * 所有的选项
   */
  options: (IOption | IOptionGroup)[]
  color?: string
  category?: string
  seriesId?: string
  className?: string
  /**
   * 默认的文案
   */
  defaultText?: string
  /**
   * @param option 被选中选项
   * @param index 被选项序号
   */
  onChange(option: IOption, index: number): void
  renderLink?(value: number | string | boolean): Wrapper
}

export default function Select(props: IProps) {
  const {
    value,
    options,
    onChange,
    className,
    defaultText = '',
    color,
    category,
    seriesId,
    renderLink,
  } = props
  // 选项框是否打开
  const [open, setOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    function handleDocumentClick(evt: Event) {
      if (!ref.current || ref.current.contains(evt.target as Node)) {
        return
      }
      setOpen(false)
    }
    on(document.body, 'click', handleDocumentClick)
    return () => {
      off(document.body, 'click', handleDocumentClick)
    }
  }, [])

  function handleOptionClick(
    evt: React.MouseEvent,
    option: IOption,
    index: number
  ) {
    onChange(option, index)
    setOpen(false)
  }

  let selectedOption: IOption | undefined
  options.forEach((item) => {
    if (typeof (item as IOptionGroup).label === 'string') {
      const finded = (item as IOptionGroup).options.find((inner) => {
        return inner.value === value
      })
      if (finded) {
        selectedOption = finded
      }
    }
    if ((item as IOption).value === value) {
      selectedOption = item as IOption
    }
  })

  return (
    <div
      ref={ref}
      className={classnames(className, 'select')}
      onClick={() => {
        // 打开option弹窗
        setOpen(!open)
      }}
    >
      <span className="text">
        {selectedOption ? selectedOption.text : defaultText}
      </span>
      <span className="icon-up-arrow-ic arrow-down" />
      {open && (
        <div className="list">
          {options?.map((item, index) => {
            if (typeof (item as IOptionGroup).label === 'string') {
              return (
                <OptionGroup
                  key={(item as IOptionGroup).label}
                  label={(item as IOptionGroup).label}
                  options={(item as IOptionGroup).options}
                  selected={selectedOption}
                  onClickHandler={handleOptionClick}
                  color={color}
                  category={category}
                  seriesId={seriesId}
                  renderLink={renderLink}
                />
              )
            }
            return (
              <Option
                onClickHandler={handleOptionClick}
                index={index}
                active={
                  selectedOption &&
                  selectedOption.value === (item as IOption).value
                }
                value={(item as IOption).value}
                text={(item as IOption).text}
                key={(item as IOption).text}
                color={color}
                category={category}
                seriesId={seriesId}
                renderLink={renderLink}
              />
            )
          })}
        </div>
      )}
      <style jsx>
        {`
          .select {
            position: relative;
            display: inline-flex;
            align-items: center;
            padding: 8px;
            padding-left: 12px;
            line-height: 26px;
            text-align: center;
            border-radius: 4px;
            border: solid 1px #ededed;
            cursor: pointer;
            use-select: none;
          }
          .arrow-down {
            transform: rotate(180deg);
          }
          .text {
            flex-grow: 1;
            font-size: 14px;
            line-height: 20px;
            font-weight: 500;
          }
          .list {
            position: absolute;
            top: 40px;
            right: 0;
            z-index: 2;
            width: 100%;
            padding: 12px 0;
            max-height: 480px;
            overflow-y: auto;
            background: #fff;
            cursor: pointer;
            box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.08);
          }
          .item {
            padding: 4px 12px;
            text-align: left;
          }
          .item:hover {
            font-weight: 500;
            background-color: #ffe100;
          }
        `}
      </style>
    </div>
  )
}

function Option(props: {
  value: number | string | boolean
  text: string
  index: number
  active?: boolean
  className?: string
  onClickHandler: OptionClickHandler
  color?: string
  category?: string
  seriesId?: string
  renderLink?(value: number | string | boolean): Wrapper
}) {
  const {
    index,
    value,
    text,
    active,
    className,
    onClickHandler,
    color,
    category,
    seriesId,
    renderLink,
  } = props
  return (renderLink ? renderLink(value) : noopWrapper)(
    <div
      className={classnames('option tw-text-common-black', className, {
        default: active,
      })}
      onClick={(evt) => {
        onClickHandler(
          evt,
          {
            text,
            value,
          },
          index
        )
      }}
    >
      {text}
      <style jsx>
        {`
          .option {
            line-height: 28px;
            padding: 4px 12px;
            font-size: 14px;
          }
          .option:hover,
          .default {
            background-color: #ffe100;
            font-weight: 500;
          }
        `}
      </style>
    </div>
  ) as React.ReactElement
}
function OptionGroup(props: {
  label: string
  options: IOptionGroup['options']
  selected?: IOption
  color?: string
  category?: string
  seriesId?: string
  onClickHandler: OptionClickHandler
  renderLink?(value: number | string | boolean): Wrapper
}) {
  const {
    label,
    options,
    selected,
    onClickHandler,
    color,
    category,
    seriesId,
    renderLink,
  } = props
  return (
    <div className={classnames('group')}>
      <div className="label">{label}</div>
      <div className="options">
        {options.map((option, index) => {
          return (
            <Option
              key={option.text}
              text={option.text}
              value={option.value}
              index={index}
              onClickHandler={onClickHandler}
              active={selected && selected.value === option.value}
              color={color}
              category={category}
              seriesId={seriesId}
              renderLink={renderLink}
            />
          )
        })}
      </div>
      <style jsx>
        {`
          .label {
            line-height: 28px;
            background-color: #f1f1f1;
            padding: 0 12px;
          }
          .options {
            padding: 8px 0;
          }
        `}
      </style>
    </div>
  )
}
